<template>
  <div :class="showCount ? 'flex items-center justify-between gap-4 mt-4' : 'flex items-center justify-end gap-2 mr-2'">
    <div v-if="showCount" class="flex items-center gap-2">
      <p class="text-text-secondary text-xs">
        {{ $t('shared.pagination.showing', {
          from: (currentPage - 1) * perPage + 1,
          to: Math.min(currentPage * perPage, totalCount),
          total: totalCount,
          items: itemName,
        }) }}
      </p>
    </div>

    <div v-if="totalPages > 1" class="flex items-center justify-end gap-2">
      <button
        class="p-2 text-text-secondary hover:text-text-primary disabled:opacity-50 disabled:hover:text-text-secondary"
        :disabled="currentPage === 1"
        @click="$emit('changePage', currentPage - 1)"
      >
        <div class="flex bg-gray-700 p-2 rounded-lg">
          <ArrowLeftIcon class="h-4 w-4 text-text-secondary" />
        </div>
      </button>

      <button
        v-for="pageNum in displayedPages"
        :key="pageNum"
        @click="$emit('changePage', pageNum)"
        class="px-3 py-1 rounded-md text-sm"
        :class="currentPage === pageNum ? 'text-primary-500' : 'text-text-secondary hover:text-text-primary'"
      >
        {{ pageNum }}
      </button>

      <span v-if="totalPages > maxVisiblePages && !displayedPages.includes(totalPages)" class="text-text-secondary">...</span>

      <button
        v-if="totalPages > maxVisiblePages && !displayedPages.includes(totalPages)"
        @click="$emit('changePage', totalPages)"
        class="px-3 py-1 rounded-md text-sm text-text-secondary hover:text-text-primary"
        :class="{ 'bg-primary-600 text-text-primary': currentPage === totalPages }"
      >
        {{ totalPages }}
      </button>

      <button
        class="p-2 text-text-secondary hover:text-text-primary disabled:opacity-50 disabled:hover:text-text-secondary"
        :disabled="currentPage === totalPages"
        @click="$emit('changePage', currentPage + 1)"
      >
        <div class="flex bg-gray-700 p-2 rounded-lg">
          <ArrowRightIcon class="h-4 w-4 text-text-secondary" />
        </div>
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/vue/20/solid';

const props = defineProps({
  itemName: {
    type: String,
    default: '',
  },
  currentPage: {
    type: Number,
    required: true,
  },
  perPage: {
    type: Number,
    default: 0,
  },
  totalCount: {
    type: Number,
    default: 0,
  },
  totalPages: {
    type: Number,
    required: true,
  },
  maxVisiblePages: {
    type: Number,
    default: 3,
  },
  showCount: {
    type: Boolean,
    default: true,
  },
});

defineEmits(['changePage']);

const displayedPages = computed(() => {
  if (props.totalPages <= props.maxVisiblePages) {
    return Array.from({ length: props.totalPages }, (_, i) => i + 1);
  }

  let start = Math.max(1, props.currentPage - Math.floor(props.maxVisiblePages / 2));
  const end = Math.min(start + props.maxVisiblePages - 1, props.totalPages);

  if (end - start + 1 < props.maxVisiblePages) {
    start = Math.max(1, end - props.maxVisiblePages + 1);
  }

  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
});
</script>
